/**
 * AdminAuditLog — Phase 14 Wave 1, Module H
 * Read-only feed of every admin write action (user role changes, deal
 * overrides, integration-key changes, etc). Backed by `/api/admin/audit-log`.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronRight, Download, Loader2, RefreshCw, Shield } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function downloadCsv(rows) {
  const header = ["created_at", "actor_user_id", "actor_email", "action", "target_collection", "target_id"];
  const lines = [header.join(",")];
  for (const r of rows) {
    const v = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    lines.push([r.created_at, r.admin_user_id || r.actor_user_id, r.actor_email,
      r.action, r.target_collection, r.target_id].map(v).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `audit_log_${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (actionFilter.trim()) params.action = actionFilter.trim();
      const { data } = await api.get("/admin/audit-log", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Failed to load audit log");
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [skip, limit, actionFilter]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-6 fade-in" data-testid="admin-audit-log">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Audit</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">Audit Log</h1>
          <p className="mt-1 text-sm text-slate-500">
            Immutable trail of every admin write action — used for compliance and forensics.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadCsv(rows)} disabled={!rows.length} data-testid="audit-export">
            <Download className="mr-1 h-3.5 w-3.5" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="audit-refresh">
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-slate-600">Filter by action (substring)</label>
          <Input
            value={actionFilter}
            onChange={(e) => { setSkip(0); setActionFilter(e.target.value); }}
            placeholder="e.g. integration.set, user.delete"
            className="mt-1"
            data-testid="audit-action-filter"
          />
        </div>
        <p className="text-xs text-slate-500">{total} entries · showing {rows.length}</p>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="w-8 px-3 py-2.5"></th>
              <th className="px-3 py-2.5 text-left">When</th>
              <th className="px-3 py-2.5 text-left">Admin</th>
              <th className="px-3 py-2.5 text-left">Action</th>
              <th className="px-3 py-2.5 text-left">Target</th>
              <th className="px-3 py-2.5 text-left">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan="6" className="px-3 py-12 text-center text-slate-400"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
            ) : !rows.length ? (
              <tr><td colSpan="6" className="px-3 py-12 text-center text-slate-400">No entries yet.</td></tr>
            ) : rows.map((r) => {
              const key = r.id || r._id || `${r.created_at}-${r.action}`;
              const isOpen = !!expanded[key];
              return (
                <React.Fragment key={key}>
                  <tr className="hover:bg-slate-50">
                    <td className="px-3 py-2.5">
                      <button onClick={() => toggle(key)} className="text-slate-400 hover:text-slate-700" data-testid={`audit-row-toggle-${key}`}>
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{r.actor_email || r.admin_user_id || r.actor_user_id || "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                        <Shield className="h-3 w-3" /> {r.action}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs font-mono text-slate-500">
                      {r.target_collection || "—"}{r.target_id ? ` · ${r.target_id.slice(0, 8)}` : ""}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-400">{r.ip || "—"}</td>
                  </tr>
                  {isOpen ? (
                    <tr className="bg-slate-50">
                      <td colSpan="6" className="px-3 py-3">
                        <div className="grid gap-3 text-xs sm:grid-cols-2">
                          <div>
                            <p className="mb-1 font-semibold text-slate-600">Before</p>
                            <pre className="overflow-x-auto rounded-md border border-slate-200 bg-white p-2 font-mono text-[11px] text-slate-700">{JSON.stringify(r.before ?? null, null, 2)}</pre>
                          </div>
                          <div>
                            <p className="mb-1 font-semibold text-slate-600">After</p>
                            <pre className="overflow-x-auto rounded-md border border-slate-200 bg-white p-2 font-mono text-[11px] text-slate-700">{JSON.stringify(r.after ?? null, null, 2)}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <Button variant="outline" size="sm" disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="audit-prev">
          Previous
        </Button>
        <span className="text-xs text-slate-500">{skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
        <Button variant="outline" size="sm" disabled={skip + limit >= total || loading} onClick={() => setSkip(skip + limit)} data-testid="audit-next">
          Next
        </Button>
      </div>
    </div>
  );
}
